import { useState } from 'react';
import { Button } from './ui/button';
import { BadgeCheck, Copy, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerifiedBadgeShare = ({ profile }) => {
  const [copied, setCopied] = useState('');

  if (!profile || profile.verification_status !== 'verified') return null;

  const profileUrl = `${window.location.origin}/facilitators/${profile.id}`;
  const badgeImageUrl = `${BACKEND_URL}/api/facilitators/${profile.id}/badge.png`;
  const altText = `${profile.full_name} is Verified on Campia`;
  const embedCode = `<a href="${profileUrl}" target="_blank" rel="noopener">\n  <img src="${badgeImageUrl}" alt="${altText}" width="280" height="75" style="border:0;" />\n</a>`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-6" data-testid="verified-badge-share">
      <div className="flex items-center gap-2 mb-2">
        <BadgeCheck className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-semibold text-xl">Share your verified badge</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        You're verified on Campia. Show it off on your website, LinkedIn or email signature — the badge links back to your public profile.
      </p>

      {/* Live preview */}
      <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="inline-block mb-6">
        <img
          src={badgeImageUrl}
          alt={altText}
          className="h-[75px] w-auto rounded-md border border-border"
          data-testid="badge-preview-img"
        />
      </a>

      {/* Embed code */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium">Embed code (for your website)</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full h-8"
            onClick={() => copy(embedCode, 'embed')}
            data-testid="copy-embed-code-btn"
          >
            {copied === 'embed' ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
          </Button>
        </div>
        <pre className="text-xs bg-muted/60 border border-border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-code-text">{embedCode}</pre>
      </div>

      {/* Direct image URL */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium">Direct image URL (for LinkedIn / email signature)</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full h-8"
            onClick={() => copy(badgeImageUrl, 'url')}
            data-testid="copy-image-url-btn"
          >
            {copied === 'url' ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
          </Button>
        </div>
        <code className="block text-xs bg-muted/60 border border-border rounded-lg p-3 overflow-x-auto break-all" data-testid="image-url-text">{badgeImageUrl}</code>
      </div>
    </div>
  );
};

export default VerifiedBadgeShare;
